/**
 * DateInput — standardised date field for all modals
 *
 * Renders a masked text input (dd/MM/yyyy) with a hidden native
 * <input type="date"> triggered by the calendar icon button.
 *
 * Why two inputs?
 *   - The visible text input gives a consistent look across browsers/OS and
 *     avoids the iOS "spinning wheels" UX from native date pickers.
 *   - The hidden native input is used only when the user taps the calendar
 *     icon, so the native picker can still be opened on mobile for convenience.
 *   - fontSize: 16px prevents iOS from auto-zooming into the field.
 *
 * value / onChange always use internal "yyyy-MM-dd" format, matching the
 * format expected by Supabase and the rest of the app.
 */
import React, { useEffect, useRef, useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { applyDateMask, convertDisplayToInternal, convertInternalToDisplay } from '@/utils/dateTimeInputUtils';
import { cn } from '@/lib/utils';

interface DateInputProps {
  value: string;                       // Internal "yyyy-MM-dd"
  onChange: (value: string) => void;   // Emits "yyyy-MM-dd"
  id?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  inputClassName?: string;
  dataSignupField?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  onBlur?: () => void;
  min?: string;
  max?: string;
}

export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  id = 'date-input',
  disabled = false,
  required = false,
  className = '',
  inputClassName,
  dataSignupField,
  ariaInvalid,
  ariaDescribedBy,
  onBlur,
  min,
  max,
}) => {
  const [display, setDisplay] = useState(() => convertInternalToDisplay(value));
  const hiddenRef = useRef<HTMLInputElement>(null);

  // Sync display when value changes externally (e.g. edit mode pre-fill)
  useEffect(() => {
    setDisplay(convertInternalToDisplay(value));
  }, [value]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setDisplay(masked);
    const internal = convertDisplayToInternal(masked);
    if (internal) onChange(internal);
    else if (masked.length === 0) onChange('');
  };

  const openPicker = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const input = hiddenRef.current;
    if (!input) return;
    input.value = value || '';
    input.focus();
    if (typeof (input as any).showPicker === 'function') {
      try { (input as any).showPicker(); } catch { input.click(); }
    } else {
      input.click();
    }
  };

  return (
    <div
      className={`relative w-full ${className}`}
      style={{ height: 48, margin: 0, padding: 0 }}
    >
      {/* Visible masked text input */}
      <input
        id={id}
        type="text"
        value={display}
        onChange={handleTextChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        placeholder="dd/MM/yyyy"
        inputMode="numeric"
        autoComplete="off"
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        data-signup-field={dataSignupField}
        className={cn(
          'text-black bg-white text-sm w-full focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-50',
          className?.includes('border-0') ? '' : 'border border-gray-300 rounded-md',
          inputClassName,
        )}
        style={{
          height: 48,
          minHeight: 48,
          maxHeight: 48,
          margin: 0,
          padding: '0.5rem 2.5rem 0.5rem 0.75rem',
          boxSizing: 'border-box',
          fontSize: 16,   // prevents iOS auto-zoom
          lineHeight: 1.5,
          borderRadius: className?.includes('border-0') ? '0' : '0.375rem',
        }}
      />

      {/* Hidden native date picker — triggered by button */}
      <input
        ref={hiddenRef}
        type="date"
        min={min}
        max={max}
        onChange={(e) => {
          if (e.target.value) {
            onChange(e.target.value);
          }
        }}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: 48,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 1,
          margin: 0,
          padding: 0,
          border: 'none',
          background: 'transparent',
          boxSizing: 'border-box',
          outline: 'none',
        }}
        tabIndex={-1}
        aria-hidden="true"
      />

      {/* Calendar icon / picker trigger */}
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        aria-label="Open date picker"
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer bg-transparent border-0 p-0 z-20 w-6 h-6 flex items-center justify-center disabled:cursor-not-allowed"
      >
        <CalendarIcon className="w-4 h-4 pointer-events-none" />
      </button>
    </div>
  );
};
